/**
 * Firm-wide AML / ID-V dashboard.
 *
 * Single screen showing every "current" AML check across every active
 * case (one row per case+client, latest check wins). Sorted with stale
 * rows (no update ≥48h, still pending) at the top, then by bucket
 * urgency. Designed for partners to run daily AML governance in 30
 * seconds.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import API from "@/lib/api";
import Header from "@/components/app/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  ShieldCheck,
  ShieldWarning,
  ShieldSlash,
  Clock,
  Warning,
  ArrowRight,
  Funnel,
  ArrowsClockwise,
  PaperPlaneTilt,
  FileText,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const BUCKET_META = {
  clear: { label: "Clear", chipCls: "bg-emerald-100 text-emerald-700", Icon: ShieldCheck },
  warn: { label: "Refer", chipCls: "bg-amber-100 text-amber-800", Icon: ShieldWarning },
  fail: { label: "Failed", chipCls: "bg-rose-100 text-rose-700", Icon: ShieldSlash },
  open: { label: "Pending", chipCls: "bg-blue-100 text-blue-700", Icon: ShieldCheck },
};

function BucketPill({ bucket }) {
  const meta = BUCKET_META[bucket] || BUCKET_META.open;
  return (
    <Badge className={`${meta.chipCls} border-0 hover:${meta.chipCls.split(" ")[0]}`}>
      <meta.Icon weight="fill" className="w-3 h-3 mr-1" />
      {meta.label}
    </Badge>
  );
}

function StalePill({ hours }) {
  if (hours == null) return null;
  if (hours < 48) return null;
  const days = Math.floor(hours / 24);
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-rose-600 text-white">
      <Warning weight="fill" className="w-3 h-3" />
      Stale {days}d
    </span>
  );
}

export default function AmlDashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ checks: [], summary: {}, solicitors: [] });
  const [filters, setFilters] = useState({
    bucket: "all",
    solicitor_id: "all",
    stale_only: false,
    include_clear: false,
    search: "",
  });
  const [resending, setResending] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.bucket !== "all") params.bucket = filters.bucket;
      if (filters.solicitor_id !== "all") params.solicitor_id = filters.solicitor_id;
      if (filters.stale_only) params.stale_only = true;
      if (filters.include_clear) params.include_clear = true;
      const { data } = await API.get("/aml/dashboard", { params });
      setData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load AML dashboard");
    } finally {
      setLoading(false);
    }
  }, [filters.bucket, filters.solicitor_id, filters.stale_only, filters.include_clear]);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    if (!q) return data.checks;
    return data.checks.filter((c) =>
      (c.case_title || "").toLowerCase().includes(q) ||
      (c.case_reference || "").toLowerCase().includes(q) ||
      (c.client_name || "").toLowerCase().includes(q) ||
      (c.client_email || "").toLowerCase().includes(q) ||
      (c.solicitor_name || "").toLowerCase().includes(q) ||
      (c.provider_check_id || "").toLowerCase().includes(q),
    );
  }, [data.checks, filters.search]);

  const handleResend = async (check) => {
    setResending(check.id);
    const tid = toast.loading(`Re-sending to ${check.client_email}…`);
    try {
      await API.post(`/aml/${check.id}/resend-email`);
      toast.success("Verification link re-sent.", { id: tid });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Resend failed", { id: tid });
    } finally {
      setResending(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              AML / ID-V Dashboard
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Firm-wide view of every open AML check. Stale rows (≥48h with no update) are flagged at the top — chase or refresh.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/aml/audit">
              <Button variant="outline" data-testid="aml-dash-audit-link">
                <FileText weight="bold" className="w-4 h-4 mr-1" />
                Audit Log
              </Button>
            </Link>
            <Button variant="outline" onClick={load} disabled={loading} data-testid="aml-dash-refresh">
              <ArrowsClockwise weight="bold" className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stat row */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          <StatCard label="Total in view" value={data.summary?.total || 0} tone="slate" testid="aml-dash-stat-total" />
          <StatCard label="Stale" value={data.summary?.stale || 0} tone={(data.summary?.stale || 0) > 0 ? "rose" : "slate"} testid="aml-dash-stat-stale" />
          <StatCard label="Pending" value={data.summary?.pending || 0} tone="blue" testid="aml-dash-stat-pending" />
          <StatCard label="Refer" value={data.summary?.refer || 0} tone="amber" testid="aml-dash-stat-refer" />
          <StatCard label="Failed" value={data.summary?.failed || 0} tone={(data.summary?.failed || 0) > 0 ? "rose" : "slate"} testid="aml-dash-stat-failed" />
        </div>

        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4 flex flex-wrap items-end gap-3" data-testid="aml-dash-filters">
          <Funnel weight="bold" className="w-4 h-4 text-slate-400 mb-2.5" />
          <div className="space-y-1 min-w-[140px]">
            <Label className="text-[11px] uppercase tracking-wide text-slate-500">Verdict</Label>
            <Select value={filters.bucket} onValueChange={(v) => setFilters((f) => ({ ...f, bucket: v }))}>
              <SelectTrigger className="h-9 text-sm" data-testid="aml-dash-filter-bucket"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any verdict</SelectItem>
                <SelectItem value="open">Pending</SelectItem>
                <SelectItem value="warn">Refer / Consider</SelectItem>
                <SelectItem value="fail">Failed</SelectItem>
                <SelectItem value="clear">Clear</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 min-w-[180px]">
            <Label className="text-[11px] uppercase tracking-wide text-slate-500">Solicitor</Label>
            <Select value={filters.solicitor_id} onValueChange={(v) => setFilters((f) => ({ ...f, solicitor_id: v }))}>
              <SelectTrigger className="h-9 text-sm" data-testid="aml-dash-filter-solicitor"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All solicitors</SelectItem>
                {(data.solicitors || []).map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name || s.id}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 flex-1 min-w-[200px]">
            <Label className="text-[11px] uppercase tracking-wide text-slate-500">Search</Label>
            <Input
              className="h-9 text-sm"
              placeholder="Case, client, email, solicitor, ref…"
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
              data-testid="aml-dash-search"
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-700 mb-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.stale_only}
              onChange={(e) => setFilters((f) => ({ ...f, stale_only: e.target.checked }))}
              data-testid="aml-dash-stale-toggle"
            />
            Stale only (≥48h)
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-700 mb-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.include_clear}
              onChange={(e) => setFilters((f) => ({ ...f, include_clear: e.target.checked }))}
              data-testid="aml-dash-clear-toggle"
            />
            Include cleared
          </label>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="aml-dash-table">
          {loading ? (
            <div className="p-12 text-center text-sm text-slate-500">Loading AML checks…</div>
          ) : visible.length === 0 ? (
            <div className="p-12 text-center">
              <ShieldCheck weight="duotone" className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
              <p className="text-sm text-slate-600 font-medium">Nothing to chase. 🎉</p>
              <p className="text-xs text-slate-400 mt-1">
                Either no open checks match the filters, or every active check has been processed.
              </p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="text-left px-4 py-3 font-semibold">Verdict</th>
                  <th className="text-left px-4 py-3 font-semibold">Client</th>
                  <th className="text-left px-4 py-3 font-semibold">Case</th>
                  <th className="text-left px-4 py-3 font-semibold">Solicitor</th>
                  <th className="text-left px-4 py-3 font-semibold">Last update</th>
                  <th className="text-right px-4 py-3 font-semibold"></th>
                </tr>
              </thead>
              <tbody>
                {visible.map((c) => (
                  <tr
                    key={c.id}
                    className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${c.is_stale ? "bg-rose-50/30" : c.bucket === "fail" ? "bg-rose-50/30" : ""}`}
                    data-testid={`aml-dash-row-${c.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        <BucketPill bucket={c.bucket} />
                        <StalePill hours={c.hours_since_update} />
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{c.client_name || c.client_email}</div>
                      <div className="text-[11px] text-slate-500">{c.client_email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900 truncate max-w-[280px]">{c.case_title || "Untitled"}</div>
                      {c.case_reference && (
                        <div className="text-[11px] text-slate-500 font-mono">{c.case_reference}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-700">{c.solicitor_name || "—"}</td>
                    <td className="px-4 py-3 text-xs text-slate-700">
                      <div className="flex items-center gap-1">
                        <Clock weight="bold" className="w-3 h-3 text-slate-400" />
                        {new Date(c.updated_at || c.created_at).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" })}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {c.verification_url && c.bucket !== "clear" && c.bucket !== "fail" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-xs h-7"
                            onClick={() => handleResend(c)}
                            disabled={resending === c.id}
                            data-testid={`aml-dash-resend-${c.id}`}
                          >
                            <PaperPlaneTilt weight="bold" className="w-3 h-3 mr-1" />
                            Chase
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/cases/${c.case_id}?tab=aml`)}
                          className="text-[#1462a0] hover:bg-[#1462a0]/10 hover:text-[#1462a0] text-xs h-7"
                          data-testid={`aml-dash-open-${c.id}`}
                        >
                          Open <ArrowRight weight="bold" className="w-3 h-3 ml-1" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {visible.length > 0 && (
          <p className="text-xs text-slate-400 mt-3 text-center">
            Showing {visible.length} of {data.checks.length} check{data.checks.length === 1 ? "" : "s"}.
          </p>
        )}
      </main>
    </div>
  );
}

function StatCard({ label, value, tone, testid }) {
  const toneCls = {
    rose: "bg-rose-50 border-rose-200 text-rose-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    slate: "bg-white border-slate-200 text-slate-900",
  }[tone] || "bg-white border-slate-200 text-slate-900";
  return (
    <div className={`border rounded-lg p-4 ${toneCls}`} data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide opacity-80">{label}</div>
      <div className="text-3xl font-bold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>{value}</div>
    </div>
  );
}
